import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";

import type {
  OrderScriptActivityKind,
  OrderScriptPreflightComment,
} from "../types/marketplace.types";

export type ActivitySort = "newest" | "oldest";
export type ActivityFilter = "all" | "notes" | "status";

export interface ActivityDayGroup {
  dayKey: string;
  entries: OrderScriptPreflightComment[];
}

export interface SystemActivityRun {
  kind: "system";
  entries: OrderScriptPreflightComment[];
  startAt: string;
  endAt: string;
}

export interface NoteActivitySegment {
  kind: "note";
  entry: OrderScriptPreflightComment;
}

export type ActivitySegment = SystemActivityRun | NoteActivitySegment;

function startOfLocalDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Oldest first. Equal `createdAt` (Postgres `now()` is transaction-scoped)
 * puts STATUS_CHANGE before COMMENT so a revision note shares its hop's N.
 */
function compareActivityChronological(
  a: OrderScriptPreflightComment,
  b: OrderScriptPreflightComment,
): number {
  const timeDiff = Date.parse(a.createdAt) - Date.parse(b.createdAt);
  if (timeDiff !== 0) return timeDiff;
  if (a.kind !== b.kind) {
    return a.kind === "STATUS_CHANGE" ? -1 : 1;
  }
  return a.id.localeCompare(b.id);
}

/**
 * Repair `fromStatus` on a chronological STATUS_CHANGE sequence so each hop
 * starts from the previous event's `toStatus` when the stored origin is stale.
 */
export function reconcileActivityFromStatuses(
  entries: readonly OrderScriptPreflightComment[],
): OrderScriptPreflightComment[] {
  const chronological = [...entries].sort(compareActivityChronological);
  let previousTo: MarketplaceFulfillmentStatus | null = null;
  return chronological.map((entry) => {
    if (entry.kind !== "STATUS_CHANGE") return entry;
    const fromStatus =
      previousTo != null && entry.fromStatus !== previousTo
        ? previousTo
        : (entry.fromStatus ?? previousTo);
    if (entry.toStatus) previousTo = entry.toStatus;
    return fromStatus === entry.fromStatus ? entry : { ...entry, fromStatus };
  });
}

export function sortActivity(
  entries: readonly OrderScriptPreflightComment[],
  sort: ActivitySort,
): OrderScriptPreflightComment[] {
  const reconciled = reconcileActivityFromStatuses(entries);
  if (sort === "oldest") return reconciled;
  return [...reconciled].sort(
    (a, b) => compareActivityChronological(b, a),
  );
}

export function groupActivityByDay(
  entries: readonly OrderScriptPreflightComment[],
  sort: ActivitySort,
): ActivityDayGroup[] {
  const sorted = sortActivity(entries, sort);
  const groups: ActivityDayGroup[] = [];
  for (const entry of sorted) {
    const dayKey = startOfLocalDay(entry.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.dayKey === dayKey) last.entries.push(entry);
    else groups.push({ dayKey, entries: [entry] });
  }
  return groups;
}

export function filterActivity(
  entries: readonly OrderScriptPreflightComment[],
  filter: ActivityFilter,
): OrderScriptPreflightComment[] {
  if (filter === "all") return [...entries];
  const kind = filter === "notes" ? "COMMENT" : "STATUS_CHANGE";
  return entries.filter((entry) => entry.kind === kind);
}

/**
 * When a script line has multiple videos, each review thread is scoped by
 * `scriptReviewId`. Unscoped rows stay visible only on the whole-line feed.
 */
export function filterActivityForScriptReview(
  entries: readonly OrderScriptPreflightComment[],
  scriptReviewId: string | null | undefined,
  options: { lineLevelWhenUnscoped?: boolean } = {},
): OrderScriptPreflightComment[] {
  if (!scriptReviewId) return [...entries];
  const lineLevelWhenUnscoped = options.lineLevelWhenUnscoped ?? false;
  return entries.filter((entry) => {
    if (entry.scriptReviewId === scriptReviewId) return true;
    if (entry.scriptReviewId != null) return false;
    return lineLevelWhenUnscoped;
  });
}

export function segmentActivity(
  entries: readonly OrderScriptPreflightComment[],
): ActivitySegment[] {
  const segments: ActivitySegment[] = [];
  for (const entry of entries) {
    if (entry.kind === "COMMENT") {
      segments.push({ kind: "note", entry });
      continue;
    }

    const previous = segments[segments.length - 1];
    if (
      previous?.kind === "system" &&
      previous.entries[0]?.orderScriptId === entry.orderScriptId
    ) {
      previous.entries.push(entry);
      const timestamps = previous.entries.map((item) =>
        Date.parse(item.createdAt),
      );
      previous.startAt =
        previous.entries[timestamps.indexOf(Math.min(...timestamps))]
          ?.createdAt ?? previous.startAt;
      previous.endAt =
        previous.entries[timestamps.indexOf(Math.max(...timestamps))]
          ?.createdAt ?? previous.endAt;
      continue;
    }

    segments.push({
      kind: "system",
      entries: [entry],
      startAt: entry.createdAt,
      endAt: entry.createdAt,
    });
  }
  return segments;
}

function isRevisionRequestHop(entry: OrderScriptPreflightComment): boolean {
  return (
    entry.kind === "STATUS_CHANGE" &&
    entry.toStatus === "SENT_FOR_REVISION" &&
    entry.fromStatus !== "SENT_FOR_REVISION"
  );
}

export function countRevisionRequests(
  entries: readonly OrderScriptPreflightComment[],
): number {
  return reconcileActivityFromStatuses(entries).filter(isRevisionRequestHop)
    .length;
}

/**
 * Per-row revision number for each entry, counted independently per script.
 * Reconcile runs inside each script so a mixed order feed cannot cross-wire hops.
 */
export function revisionNumberByEntryId(
  entries: readonly OrderScriptPreflightComment[],
): Map<string, number> {
  const byScript = new Map<string, OrderScriptPreflightComment[]>();
  for (const entry of entries) {
    const group = byScript.get(entry.orderScriptId);
    if (group) group.push(entry);
    else byScript.set(entry.orderScriptId, [entry]);
  }

  const numbers = new Map<string, number>();
  for (const group of byScript.values()) {
    let revisionNumber = 0;
    for (const entry of reconcileActivityFromStatuses(group)) {
      if (isRevisionRequestHop(entry)) revisionNumber += 1;
      numbers.set(entry.id, revisionNumber);
    }
  }
  return numbers;
}

export function isHumanActivity(kind: OrderScriptActivityKind): boolean {
  return kind === "COMMENT";
}
